import { type ReactElement, type ReactNode } from 'react';
import { formatRangeWindowNoDataText } from '@/features/quality/hooks';
import { StatusPresentationLoadingState } from '@/features/shared/components/StatusPresentationLoadingState';
import { Button } from '@/ui/primitives';
import { UploadFileIcon } from '@/ui/primitives/icons';
import { EntityTrendsPageContentSection } from './EntityTrendsPageContentSection';
import { EntityTrendsUploadDialog } from './EntityTrendsUploadDialog';
import type { EntityTrendsPageViewModel } from '@/features/shared/hooks/useEntityTrendsPage';

type EntityTrendsPageShellProps<TEntityItem> = {
  viewModel: EntityTrendsPageViewModel<TEntityItem>;
  title: string;
  addButtonLabel: string;
  searchAriaLabel: string;
  chartTabsAriaLabel: string;
  pageTabsAriaLabel: string;
  entityTabLabel: string;
  chartEmptyStateTitle: string;
  entityTableNoRowsText: string;
  executionTableNoRowsText: string;
  renderEntityTable: (input: { showDeltas: boolean; noRowsText: string }) => ReactElement;
  renderExecutionsTable: (input: { noRowsText: string }) => ReactElement;
  contentAbove?: ReactNode;
  contentBelow?: ReactNode;
  contentOverride?: ReactNode;
};

export const EntityTrendsPageShell = <TEntityItem,>({
  viewModel,
  title,
  addButtonLabel,
  searchAriaLabel,
  chartTabsAriaLabel,
  pageTabsAriaLabel,
  entityTabLabel,
  chartEmptyStateTitle,
  entityTableNoRowsText,
  executionTableNoRowsText,
  renderEntityTable,
  renderExecutionsTable,
  contentAbove,
  contentBelow,
  contentOverride,
}: EntityTrendsPageShellProps<TEntityItem>): ReactElement => {
  if (viewModel.showPageSkeleton) {
    return <StatusPresentationLoadingState message={viewModel.loadingMessage} />;
  }

  const titleActions = (
    <Button
      startIcon={<UploadFileIcon />}
      fullWidthOnMobile={false}
      onClick={viewModel.uploadFlow.openUploadDialog}
    >
      Upload Results Files
    </Button>
  );
  const uploadDialogNode = (
    <EntityTrendsUploadDialog
      selectedProjectId={viewModel.selectedProjectId}
      projectOptions={viewModel.projectOptions}
      uploadProjectIdFieldId={viewModel.uploadProjectIdFieldId}
      uploadFlow={viewModel.uploadFlow}
    />
  );
  const resolvedChartEmptyStateTitle = formatRangeWindowNoDataText(
    chartEmptyStateTitle,
    viewModel.range,
    viewModel.hasCustomDates,
  );
  const resolvedEntityTableNoRowsText = formatRangeWindowNoDataText(
    entityTableNoRowsText,
    viewModel.range,
    viewModel.hasCustomDates,
  );
  const resolvedExecutionTableNoRowsText = formatRangeWindowNoDataText(
    executionTableNoRowsText,
    viewModel.range,
    viewModel.hasCustomDates,
  );

  return (
    <EntityTrendsPageContentSection
      viewModel={viewModel}
      titleActions={titleActions}
      footer={uploadDialogNode}
      title={title}
      addButtonLabel={addButtonLabel}
      searchAriaLabel={searchAriaLabel}
      chartTabsAriaLabel={chartTabsAriaLabel}
      pageTabsAriaLabel={pageTabsAriaLabel}
      entityTabLabel={entityTabLabel}
      chartEmptyStateTitle={resolvedChartEmptyStateTitle}
      entityTableNoRowsText={resolvedEntityTableNoRowsText}
      executionTableNoRowsText={resolvedExecutionTableNoRowsText}
      contentAbove={contentAbove}
      contentBelow={contentBelow}
      contentOverride={contentOverride}
      renderEntityTable={renderEntityTable}
      renderExecutionsTable={renderExecutionsTable}
    />
  );
};
